import React, { useState, useContext, useRef, useEffect } from "react";
import {
  faLayerGroup,
  faAngleLeft,
  faPlus,
  faChevronLeft,
  faChevronRight,
  faEllipsisV,
  faEdit,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { ProductContext } from "./../../../../store"; // Assuming you have a ProductContext
import { PostData } from "../../../../services/FetchData";
import { toast } from "react-toastify";
import {
  Button,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";

const PLATFORMS = { MAGENTO: "magento", SHOPIFY: "shopify", BIGCOMMERCE: "bigcommerce", WOOCOMMERCE: "woocommerce", DRUPAL: "drupal" };

const getMerchandisingApiConfig = (platform) => {
  const base = platform === PLATFORMS.MAGENTO ? "ms2" : "ms3";
  const prefix = platform === PLATFORMS.MAGENTO ? "merchandising" : platform;
  const path = (endpoint) => `api/v1/${prefix}/${endpoint}`;
  const listEndpoint = platform === PLATFORMS.SHOPIFY ? "getCollectionsMerchandising" : "getCategoriesMerchandising";
  const deleteEndpoint = platform === PLATFORMS.SHOPIFY ? "deleteCollection" : "deleteCategory";
  return { base, prefix, path, listEndpoint, deleteEndpoint };
};

const Categories = () => {
  const [headerButtonName, setHeaderButtonName] = useState(
    "Categories Merchandising Campaigns"
  );
  const navigate = useNavigate();
  const [pageLoader, setPageLoader] = useState(true),
    [categoryMerchandising, setCategoryMerchandising] = useState([]),
    [totalCategoryMerchandising, setTotalCategoryMerchandising] = useState(0),
    [categories, setCategories] = useState({}),
    [deleteModal, setDeleteModal] = useState(false),
    [deleteLoader, setDeleteLoader] = useState(false),
    [deleteId, setDeleteId] = useState(null),
    [currentPage, setCurrentPage] = useState(1),
    [itemsPerPageOptions] = useState([15, 20, 25, 30]),
    [itemsPerPage, setItemsPerPage] = useState(15),
    [triggerPagination, setTriggerPagination] = useState(true),
    {
      currentWebsiteId,
      websiteData,
      magentoStoreViews,
      magentoStoreViewsLoader,
      defaultStoreView,
      channels,
      channelsLoader,
    } = useContext(ProductContext),
    [openDropdowns, setOpenDropdowns] = useState({}),
    platform = websiteData?.[currentWebsiteId]?.websiteType,
    apiConfig = platform ? getMerchandisingApiConfig(platform) : null,
    toggleDropdown = (id) => {
      setOpenDropdowns((prev) => ({ ...prev, [id]: !prev[id] }));
    };

  const canLoad = platform === PLATFORMS.MAGENTO ? !magentoStoreViewsLoader : (platform === PLATFORMS.BIGCOMMERCE || platform === PLATFORMS.WOOCOMMERCE) ? !channelsLoader : true;

  const handleButtonClick = () => {
    navigate("/search/merchandising");
  };

  const onItemsPerPageChange = (newRange) => {
    setCurrentPage(1);
    setItemsPerPage(newRange);
    setTriggerPagination(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    setTriggerPagination(true);
  };

  useEffect(() => {
    if (currentWebsiteId && triggerPagination && apiConfig && canLoad) {
      setTriggerPagination(false);
      getCategoriesMerchandising();
    }
  }, [currentWebsiteId, platform, triggerPagination, apiConfig, canLoad]);

  const getCategoriesMerchandising = async () => {
    setPageLoader(true);
    const payload = platform === PLATFORMS.MAGENTO
      ? { websiteId: currentWebsiteId, page: currentPage, limit: itemsPerPage, storeView: defaultStoreView }
      : { websiteId: currentWebsiteId, page: currentPage, limit: itemsPerPage };
    PostData(apiConfig.base, apiConfig.path(apiConfig.listEndpoint), payload)
      .then((response) => {
        if (response !== "Invalid" && response !== "undefined") {
          if (response.status === "success") {
            const data = response.data;
            setCategoryMerchandising(platform === PLATFORMS.SHOPIFY ? data.collectionMerchandising : data.categoryMerchandising);
            setCategories(data.categories ?? {});
            if (data.totalMerchandising != null) setTotalCategoryMerchandising(data.totalMerchandising);
          } else if (response.status === "failure") {
            toast.error(response.message);
          } else {
            toast.error(response.message?.message || response.message || "Failed to load categories");
          }
        } else {
          toast.error("Unable to connect. Check CORS or backend availability.");
        }
        setPageLoader(false);
      })
      .catch((err) => {
        console.error("getCategoriesMerchandising error:", err);
        toast.error("Failed to fetch categories. Check your connection and that the backend is running.");
        setPageLoader(false);
      });
  };

  const handleAddButtonClick = () => {
    return navigate("/search/merchandising/category/edit", {
      state: { type: "new" },
    });
  };

  const handleEditClick = (categoryMerchandisingId) => {
    return navigate("/search/merchandising/category/edit", {
      state: { type: "edit", categoryMerchandisingId },
    });
  };

  const handleDeleteClick = (categoryMerchandisingId) => {
    setDeleteId(categoryMerchandisingId);
    setDeleteModal(true);
  };

  const deleteMerchandising = async () => {
    if (!apiConfig) return;
    setDeleteLoader(true);
    const data = platform === PLATFORMS.SHOPIFY
      ? { collectionMerchandisingId: deleteId, websiteId: currentWebsiteId }
      : { categoryMerchandisingId: deleteId, websiteId: currentWebsiteId };
    PostData(apiConfig.base, apiConfig.path(apiConfig.deleteEndpoint), data)
      .then((result) => {
        if (result !== "Invalid" && result !== "undefined") {
          if (result.status === "success") {
            toast.success(result.message);
            setTriggerPagination(true);
          } else if (result.status === "failure") {
            toast.error(result.message);
          } else {
            toast.error(result.message?.message || result.message || "Delete failed");
          }
        } else {
          toast.error("Unable to connect. Check CORS or backend availability.");
        }
        setDeleteLoader(false);
        setDeleteId(null);
        setDeleteModal(false);
      })
      .catch((err) => {
        console.error("deleteMerchandising error:", err);
        toast.error("Failed to delete. Check your connection.");
        setDeleteLoader(false);
        setDeleteId(null);
        setDeleteModal(false);
      });
  };

  return (
    <div className="merchandisingCategoriesWrapper">
      <div className="headerWrapper">
        <div className="categoriesHeader">
          <Button
            onClick={handleButtonClick}
            style={{ background: "transparent", color: "#333" }}
          >
            <FontAwesomeIcon icon={faAngleLeft} />
            <FontAwesomeIcon icon={faLayerGroup} />
            {headerButtonName}
          </Button>
        </div>
      </div>
      <div className="categoriesContent">
        <p>
          Fine tune in smart Categories pages by promoting products to the top,
          burying products at the bottom or hiding them from the results
        </p>
        <Button
          className="addButton cta autoWidth"
          onClick={handleAddButtonClick}
        >
          <span>
            <FontAwesomeIcon size="lg" icon={faPlus} />
          </span>{" "}
          Add
        </Button>
        {pageLoader ? (
          <DetailSkeleton />
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  {platform === "magento" && <th>Store View</th>}
                  {platform === "bigcommerce" && <th>Channel</th>}
                  <th>Pinned</th>
                  <th>Promoted</th>
                  <th>Buried</th>
                  <th>Hidden</th>
                  <th>Total in collection</th>
                  <th>Last Modified</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {categoryMerchandising.length > 0 ? (
                  categoryMerchandising.map((item) => (
                    <tr key={item.categoryMerchandisingId}>
                      <td>
                        {
                          categories[
                          platform === "shopify"
                            ? item.collectionId
                            : item.categoryId
                          ]
                        }
                      </td>
                      {platform === "magento" && (
                        <td>{magentoStoreViews[item.storeView]}</td>
                      )}
                      {platform === "bigcommerce" && (
                        <td>
                          {item.channelId +
                            (channels && channels[item.channelId] ? " - " + channels[item.channelId].name : "")}
                        </td>
                      )}
                      <td>
                        <div className="searchItemStatusWrp">
                          {item.pinnedItems &&
                            !isNaN(
                              Object.keys(item.pinnedItems).length -
                              Number(item.promoted) -
                              Number(item.buried)
                            )
                            ? Object.keys(item.pinnedItems).length -
                            Number(item.promoted) -
                            Number(item.buried)
                            : 0}
                        </div>
                      </td>
                      <td>{item.promoted}</td>
                      <td>{item.buried}</td>
                      <td>{item.hidden}</td>
                      <td>{item.totalInCollections}</td>
                      <td>{format(item.updatedAt, "yyyy-MM-dd HH:mm")}</td>
                      <td>
                        <Dropdown
                          key={
                            item.categoryMerchandisingId ??
                            item.collectionMerchandisingId
                          }
                          isOpen={
                            openDropdowns[
                            item.categoryMerchandisingId ??
                            item.collectionMerchandisingId
                            ] || false
                          }
                          toggle={() =>
                            toggleDropdown(
                              item.categoryMerchandisingId ??
                              item.collectionMerchandisingId
                            )
                          }
                          direction="down"
                        >
                          <DropdownToggle
                            tag="div"
                            style={{ cursor: "pointer" }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 128 512"
                              fill="#333"
                              width={"18px"}
                              height={"18px"}
                            >
                              <path d="M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z" />
                            </svg>
                          </DropdownToggle>
                          <DropdownMenu>
                            <DropdownItem
                              onClick={() =>
                                platform === "shopify"
                                  ? handleEditClick(
                                    item.collectionMerchandisingId
                                  )
                                  : handleEditClick(
                                    item.categoryMerchandisingId
                                  )
                              }
                            >
                              <span className="dropDownItemWrapper">
                                <FontAwesomeIcon icon={faEdit} /> Edit Category
                              </span>
                            </DropdownItem>
                            <DropdownItem
                              onClick={() =>
                                platform === "shopify"
                                  ? handleDeleteClick(
                                    item.collectionMerchandisingId
                                  )
                                  : handleDeleteClick(
                                    item.categoryMerchandisingId
                                  )
                              }
                            >
                              <span className="dropDownItemWrapper">
                                <FontAwesomeIcon icon={faTrash} /> Delete Rule
                              </span>
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={platform === "magento" ? 9 : 8}>
                      <Row className="justify-content-center text-center mt-4">
                        <Col md="auto">
                          <svg
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            width="128"
                            height="128"
                          >
                            <path
                              d="M0 0 C4.29 0 8.58 0 13 0 C13 11.55 13 23.1 13 35 C15.31 35 17.62 35 20 35 C19.99214478 33.78098999 19.99214478 33.78098999 19.98413086 32.53735352 C19.96343006 28.87907678 19.95036197 25.22081233 19.9375 21.5625 C19.92912109 20.28310547 19.92074219 19.00371094 19.91210938 17.68554688 C19.90888672 16.47060547 19.90566406 15.25566406 19.90234375 14.00390625 C19.89448853 12.31503296 19.89448853 12.31503296 19.88647461 10.59204102 C20 8 20 8 21 7 C22.4887186 6.91330568 23.981445 6.89296506 25.47265625 6.90234375 C26.28226807 6.90484131 27.09187988 6.90733887 27.92602539 6.90991211 C28.96106201 6.91901611 29.99609863 6.92812012 31.0625 6.9375 C34.341875 6.958125 37.62125 6.97875 41 7 C41 16.24 41 25.48 41 35 C42.32 35 43.64 35 45 35 C45 25.76 45 16.52 45 7 C51.6 7 58.2 7 65 7 C65 16.24 65 25.48 65 35 C66.32 35 67.64 35 69 35 C69 25.76 69 16.52 69 7 C73.9190625 6.9690625 73.9190625 6.9690625 78.9375 6.9375 C79.97253662 6.928396 81.00757324 6.91929199 82.07397461 6.90991211 C83.28839233 6.90616577 83.28839233 6.90616577 84.52734375 6.90234375 C85.77632446 6.89448853 85.77632446 6.89448853 87.05053711 6.88647461 C89 7 89 7 90 8 C90.08758003 9.99964982 90.10696576 12.0023611 90.09765625 14.00390625 C90.09282227 15.82631836 90.09282227 15.82631836 90.08789062 17.68554688 C90.07532227 19.60463867 90.07532227 19.60463867 90.0625 21.5625 C90.05798828 22.84576172 90.05347656 24.12902344 90.04882812 25.45117188 C90.03699545 28.63416022 90.02051068 31.81705762 90 35 C92.31 35 94.62 35 97 35 C97 23.45 97 11.9 97 0 C101.29 0 105.58 0 110 0 C110 7.92 110 15.84 110 24 C108.68 24.33 107.36 24.66 106 25 C105 24 105 24 104.88647461 22.05053711 C104.89432983 20.8015564 104.89432983 20.8015564 104.90234375 19.52734375 C104.90717773 18.17866211 104.90717773 18.17866211 104.91210938 16.80273438 C104.92467773 15.38444336 104.92467773 15.38444336 104.9375 13.9375 C104.94201172 12.98810547 104.94652344 12.03871094 104.95117188 11.06054688 C104.9629989 8.70696935 104.97947975 6.35351523 105 4 C104.01 4 103.02 4 102 4 C102 40.96 102 77.92 102 116 C102.99 116 103.98 116 105 116 C105 87.62 105 59.24 105 30 C106.32 29.67 107.64 29.34 109 29 C110.83357444 30.83357444 110.11999967 33.39635695 110.12442017 35.8359375 C110.11902725 37.09289429 110.11902725 37.09289429 110.11352539 38.37524414 C110.11367142 39.25321152 110.11381744 40.13117889 110.1139679 41.03575134 C110.11326844 43.95224435 110.10547455 46.86867433 110.09765625 49.78515625 C110.09579218 51.80154892 110.09436823 53.81794204 110.09336853 55.83433533 C110.08954244 61.15265138 110.07971139 66.4709403 110.06866455 71.78924561 C110.05845238 77.21170688 110.05387133 82.63417252 110.04882812 88.05664062 C110.0380892 98.70443903 110.02101169 109.35221726 110 120 C107.36 120 104.72 120 102 120 C102 122.64 102 125.28 102 128 C97.05 128 92.1 128 87 128 C85 124 85 124 85 120 C65.53 120.33 46.06 120.66 26 121 C25.01 123.31 24.02 125.62 23 128 C18.05 128 13.1 128 8 128 C8 125.36 8 122.72 8 120 C5.36 120 2.72 120 0 120 C0 104.49 0 88.98 0 73 C1.65 73 3.3 73 5 73 C5 87.19 5 101.38 5 116 C5.99 116 6.98 116 8 116 C8 79.04 8 42.08 8 4 C7.01 4 6.02 4 5 4 C5 25.12 5 46.24 5 68 C3.515 68.495 3.515 68.495 2 69 C1.34 68.67 0.68 68.34 0 68 C0 45.56 0 23.12 0 0 Z M25 12 C25 13.65 25 15.3 25 17 C28.63 17 32.26 17 36 17 C36 15.35 36 13.7 36 12 C32.37 12 28.74 12 25 12 Z M49 12 C49 13.65 49 15.3 49 17 C52.96 17 56.92 17 61 17 C61 15.35 61 13.7 61 12 C57.04 12 53.08 12 49 12 Z M74 12 C74 13.65 74 15.3 74 17 C77.63 17 81.26 17 85 17 C85 15.35 85 13.7 85 12 C81.37 12 77.74 12 74 12 Z M25 21 C25 22.32 25 23.64 25 25 C28.63 25 32.26 25 36 25 C36 23.68 36 22.36 36 21 C32.37 21 28.74 21 25 21 Z M49 21 C49 22.32 49 23.64 49 25 C52.96 25 56.92 25 61 25 C61 23.68 61 22.36 61 21 C57.04 21 53.08 21 49 21 Z M74 21 C74 22.32 74 23.64 74 25 C77.63 25 81.26 25 85 25 C85 23.68 85 22.36 85 21 C81.37 21 77.74 21 74 21 Z M25 29 C25 30.98 25 32.96 25 35 C28.63 35 32.26 35 36 35 C36 33.02 36 31.04 36 29 C32.37 29 28.74 29 25 29 Z M49 29 C49 30.98 49 32.96 49 35 C52.96 35 56.92 35 61 35 C61 33.02 61 31.04 61 29 C57.04 29 53.08 29 49 29 Z M74 29 C74 30.98 74 32.96 74 35 C77.63 35 81.26 35 85 35 C85 33.02 85 31.04 85 29 C81.37 29 77.74 29 74 29 Z M13 39 C13 40.32 13 41.64 13 43 C33.46 43.33 53.92 43.66 75 44 C75.495 45.485 75.495 45.485 76 47 C55.21 47 34.42 47 13 47 C13 53.6 13 60.2 13 67 C14.65 67 16.3 67 18 67 C18.02715383 65.25008666 18.04646551 63.50005055 18.0625 61.75 C18.07410156 60.77546875 18.08570313 59.8009375 18.09765625 58.796875 C18.08914661 55.7657377 18.08914661 55.7657377 17 52 C21.95 52 26.9 52 32 52 C31.67 53.32 31.34 54.64 31 56 C30.93311409 57.91561254 30.91468281 59.83335592 30.9375 61.75 C30.94652344 62.73484375 30.95554687 63.7196875 30.96484375 64.734375 C30.97644531 65.48203125 30.98804687 66.2296875 31 67 C33.31 67 35.62 67 38 67 C38.02712153 65.083412 38.04645097 63.1667127 38.0625 61.25 C38.07410156 60.18265625 38.08570312 59.1153125 38.09765625 58.015625 C38.0997216 54.80102903 38.0997216 54.80102903 37 51 C42.28 51 47.56 51 53 51 C52.34 52.65 51.68 54.3 51 56 C50.76028911 58.89651829 50.76028911 58.89651829 50.875 61.8125 C50.89304688 62.78832031 50.91109375 63.76414062 50.9296875 64.76953125 C50.95289063 65.50558594 50.97609375 66.24164063 51 67 C53.64 67 56.28 67 59 67 C59.05449838 65.27119018 59.09301688 63.54187288 59.125 61.8125 C59.14820313 60.84957031 59.17140625 59.88664062 59.1953125 58.89453125 C59.1191499 55.55344484 59.1191499 55.55344484 57 51 C62.28 51 67.56 51 73 51 C72.67 52.32 72.34 53.64 72 55 C71.93256652 57.08234592 71.91485528 59.16668557 71.9375 61.25 C71.94652344 62.32765625 71.95554688 63.4053125 71.96484375 64.515625 C71.97644531 65.33546875 71.98804688 66.1553125 72 67 C74.31 67 76.62 67 79 67 C79.02715383 65.25008666 79.04646551 63.50005055 79.0625 61.75 C79.07410156 60.77546875 79.08570312 59.8009375 79.09765625 58.796875 C79.08914661 55.7657377 79.08914661 55.7657377 78 52 C82.95 52 87.9 52 93 52 C92.67 53.32 92.34 54.64 92 56 C91.93311409 57.91561254 91.91468281 59.83335592 91.9375 61.75 C91.94652344 62.73484375 91.95554687 63.7196875 91.96484375 64.734375 C91.97644531 65.48203125 91.98804687 66.2296875 92 67 C93.65 67 95.3 67 97 67 C97 60.4 97 53.8 97 47 C91.39 47 85.78 47 80 47 C80 45.68 80 44.36 80 43 C85.61 43 91.22 43 97 43 C97 41.68 97 40.36 97 39 C69.28 39 41.56 39 13 39 Z M23 56 C23 59.63 23 63.26 23 67 C23.99 67 24.98 67 26 67 C26 63.37 26 59.74 26 56 C25.01 56 24.02 56 23 56 Z M43 56 C43 59.63 43 63.26 43 67 C44.32 67 45.64 67 47 67 C47 63.37 47 59.74 47 56 C45.68 56 44.36 56 43 56 Z M63 56 C63 59.63 63 63.26 63 67 C64.32 67 65.64 67 67 67 C67 63.37 67 59.74 67 56 C65.68 56 64.36 56 63 56 Z M84 56 C84 59.63 84 63.26 84 67 C84.99 67 85.98 67 87 67 C87 63.37 87 59.74 87 56 C86.01 56 85.02 56 84 56 Z M13 72 C13 72.99 13 73.98 13 75 C40.72 75 68.44 75 97 75 C97 74.01 97 73.02 97 72 C69.28 72 41.56 72 13 72 Z M13 80 C13 86.6 13 93.2 13 100 C15.31 100 17.62 100 20 100 C20 94.72 20 89.44 20 84 C26.6 84 33.2 84 40 84 C40.495 91.92 40.495 91.92 41 100 C42.32 100 43.64 100 45 100 C45.33 94.72 45.66 89.44 46 84 C51.94 84 57.88 84 64 84 C64.495 91.92 64.495 91.92 65 100 C66.32 100 67.64 100 69 100 C69.33 94.72 69.66 89.44 70 84 C76.6 84 83.2 84 90 84 C90 89.28 90 94.56 90 100 C92.31 100 94.62 100 97 100 C97 93.4 97 86.8 97 80 C69.28 80 41.56 80 13 80 Z M25 88 C25 91.96 25 95.92 25 100 C28.63 100 32.26 100 36 100 C36 96.04 36 92.08 36 88 C32.37 88 28.74 88 25 88 Z M49 88 C49 91.96 49 95.92 49 100 C52.96 100 56.92 100 61 100 C61 96.04 61 92.08 61 88 C57.04 88 53.08 88 49 88 Z M74 88 C74 91.96 74 95.92 74 100 C77.63 100 81.26 100 85 100 C85 96.04 85 92.08 85 88 C81.37 88 77.74 88 74 88 Z M13 104 C13 107.96 13 111.92 13 116 C40.72 116 68.44 116 97 116 C97 112.04 97 108.08 97 104 C69.28 104 41.56 104 13 104 Z M13 120 C13 121.32 13 122.64 13 124 C14.98 124 16.96 124 19 124 C19.33 122.68 19.66 121.36 20 120 C17.69 120 15.38 120 13 120 Z M90 120 C90.33 121.32 90.66 122.64 91 124 C92.98 124 94.96 124 97 124 C97 122.68 97 121.36 97 120 C94.69 120 92.38 120 90 120 Z "
                              fill="currentcolor"
                              transform="translate(9,0)"
                            />
                          </svg>
                          <p className="mt-5 mb-3">
                            No Category Merchandising Found
                          </p>
                        </Col>
                      </Row>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {totalCategoryMerchandising > 0 && (
              <>
                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex flex-1 align-items-center gap-2">
                    <Label
                      style={{ fontSize: 14, fontWeight: 400, marginBottom: 0 }}
                    >
                      Items per page
                    </Label>
                    <Input
                      type="select"
                      style={{ width: "auto" }}
                      value={itemsPerPage}
                      onChange={(e) =>
                        onItemsPerPageChange(parseInt(e.target.value, 10))
                      }
                    >
                      {itemsPerPageOptions.map((item) => {
                        return <option value={item}>{item}</option>;
                      })}
                    </Input>
                  </div>
                  {Math.ceil(totalCategoryMerchandising / itemsPerPage) > 1 && (
                    <ReactPaginate
                      nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
                      onPageChange={(event) => changePage(event.selected + 1)}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={2}
                      pageCount={Math.ceil(
                        totalCategoryMerchandising / itemsPerPage
                      )}
                      previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
                      pageClassName="page-item"
                      pageLinkClassName="page-link"
                      previousClassName="page-item"
                      previousLinkClassName="page-link"
                      nextClassName="page-item"
                      nextLinkClassName="page-link"
                      breakLabel="..."
                      breakClassName="page-item"
                      breakLinkClassName="page-link"
                      containerClassName="pagination BannerPagination"
                      activeClassName="active"
                      renderOnZeroPageCount={null}
                      forcePage={currentPage - 1}
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      {deleteModal && (
        <Modal
          isOpen={deleteModal}
          toggle={() => setDeleteModal(false)}
          backdrop="static"
          keyboard={true}
          centered={true}
        >
          <ModalHeader
            style={{ backgroundColor: "transparent", color: "#333" }}
            toggle={() => setDeleteModal(false)}
          >
            Delete
          </ModalHeader>
          <ModalBody>
            Are you sure you want to delete this merchandising?
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              outline
              onClick={() => setDeleteModal(false)}
            >
              No
            </Button>
            <Button
              color="danger"
              onClick={() => deleteMerchandising()}
              disabled={deleteLoader}
            >
              {deleteLoader ? "Deleting..." : "Yes"}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default Categories;
